import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  signal,
  ViewContainerRef,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { formDetectChanges } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { injectDevFlagSignal } from '@core/tellent-dev-flags-logic';
import { TellentDevFlag } from '@core/tellent-types';
import {
  DialogData,
  DialogRef,
  DialogService,
  DialogWidth,
  ButtonLoadingDirective,
  ButtonComponent,
  DialogFooterComponent,
  FormErrorDirective,
  IconComponent,
  InputComponent,
  LabelComponent,
  FormFieldComponent,
  DialogBodyComponent,
  DialogHeaderComponent,
} from '@core/user-interface-legacy';

import { AuthenticationDialogComponent } from '../authentication-dialog/authentication-dialog.component';
import type { AccountPasswordErrors, AccountPasswordForm } from '../password-change.service';
import { PasswordChangeService } from '../password-change.service';
import { injectAuthTranslations } from '../translations';

@Component({
  selector: 'rt-admin-profile-password-form',
  templateUrl: './admin-profile-password-form.component.html',
  styleUrls: ['./admin-profile-password-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(keydown.enter)': 'submit()',
  },
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    ReactiveFormsModule,
    FormFieldComponent,
    LabelComponent,
    InputComponent,
    IconComponent,
    FormErrorDirective,
    DialogFooterComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    TranslatePipe,
  ],
})
export class AdminProfilePasswordFormComponent {
  private readonly dialogRef = inject<DialogRef<AdminProfilePasswordFormComponent>>(DialogRef);
  private readonly dialog = inject(DialogService);
  private readonly passwordChangeService = inject(PasswordChangeService);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly data = inject<{
    secureChangeToken: string;
    canChangePasswordWithoutEmailCode?: boolean;
  }>(DialogData, { optional: true });
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  private readonly secureChangeToken: string;
  protected readonly cdRef = inject(ChangeDetectorRef);

  public translationKey = injectAuthTranslations();

  public accountPasswordForm: AccountPasswordForm;
  public errors: AccountPasswordErrors = this.passwordChangeService.errors;
  public isLoading: boolean = false;
  public focused: boolean = false;

  protected canChangePasswordWithoutEmailCode = signal<boolean | undefined>(false);
  protected usersWithoutEmails = injectDevFlagSignal(TellentDevFlag.UsersWithoutEmails);

  private ngOnDestroy$ = new Subject<void>();

  constructor() {
    const data = this.data;
    const route = this.route;

    if (data) {
      this.secureChangeToken = data.secureChangeToken;
      this.canChangePasswordWithoutEmailCode.set(data.canChangePasswordWithoutEmailCode);
    } else {
      this.secureChangeToken = route.snapshot.data.secureChangeToken;
      if (!this.secureChangeToken) {
        this.router.navigate(['.', { outlets: { dialog: [] } }]);
      }
    }
  }

  public ngOnInit(): void {
    this.accountPasswordForm = this.passwordChangeService.createForm(
      this.secureChangeToken,
      this.usersWithoutEmails() && this.canChangePasswordWithoutEmailCode(),
    );
    formDetectChanges(this.accountPasswordForm, this.cdRef, this.ngOnDestroy$);
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public submit(): void {
    if (this.accountPasswordForm.invalid) {
      return;
    }

    this.toggleLoading();
    this.passwordChangeService
      .submit(this.accountPasswordForm)
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(
        () => {
          this.toggleLoading();
          this.dialogRef.close();
          this.passwordChangeService.showSuccessNotification();
        },
        error => {
          this.toggleLoading();
          // Fallback: refresh secure change token - only if canChangePasswordWithoutEmailCode === true
          if (!this.canChangePasswordWithoutEmailCode()) {
            this.refreshSecureChangeToken();
          }
        },
      );
  }

  public getPasswordErrors(): any {
    return this.accountPasswordForm.get('password')!.errors || {};
  }

  public cancelClick(): void {
    this.dialogRef.close();
  }

  public focus(): void {
    this.focused = true;
    this.cdRef.detectChanges();
  }

  public focusOut(): void {
    this.focused = false;
    this.cdRef.detectChanges();
  }

  private refreshSecureChangeToken() {
    this.openAuthenticationDialog()
      .afterClosed.pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(secureChangeToken => {
        if (secureChangeToken) {
          this.accountPasswordForm.patchValue({ secureChangeToken });
        }
      });
  }

  private openAuthenticationDialog(): DialogRef<AuthenticationDialogComponent> {
    return this.dialog.openCustom(
      AuthenticationDialogComponent,
      { width: DialogWidth.SmallX },
      {},
      this.viewContainerRef,
    );
  }

  private toggleLoading(): void {
    this.isLoading = !this.isLoading;
    this.cdRef.detectChanges();
  }
}

export const resolveSecureChangeToken = () =>
  inject(DialogService).openCustom(AuthenticationDialogComponent, { width: DialogWidth.SmallX }, {})
    .afterClosed;
